import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faArrowLeft, 
  faPen, 
  faKey, 
  faSearch,
  faEye,
  faEyeSlash,
  faShieldAlt,
  faUsers,
  faGraduationCap,
  faBook,
  faDollarSign,
  faBoxes,
  faChartBar,
  faCog,
  faCheck
} from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../../../contexts/AuthContext';
import { perfilService } from '../../../services/perfilService';

const PerfilView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const [perfil, setPerfil] = useState(null);
  const [permissoes, setPermissoes] = useState([]);
  const [permissoesPorModulo, setPermissoesPorModulo] = useState({});
  const [loading, setLoading] = useState(true);
  const [loadingPermissoes, setLoadingPermissoes] = useState(true);
  const [error, setError] = useState('');
  const [filtroPermissoes, setFiltroPermissoes] = useState('');
  const [modulosExpandidos, setModulosExpandidos] = useState({});
  const [mostrarResumo, setMostrarResumo] = useState(true);

  useEffect(() => {
    if (hasPermission('perfis.read')) {
      loadPerfil();
      loadPermissoes();
    }
  }, [id, hasPermission]);

  // Verificar permissão após os hooks
  if (!hasPermission('perfis.read')) {
    return (
      <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
        Você não tem permissão para visualizar perfis.
      </div>
    );
  }

  const loadPerfil = async () => {
    try {
      setLoading(true);
      const response = await perfilService.getById(id);
      setPerfil(response.data);
    } catch (err) {
      setError('Erro ao carregar perfil: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const loadPermissoes = async () => {
    try {
      setLoadingPermissoes(true);
      const response = await perfilService.getPermissions(id);
      setPermissoes(response.data || []);
      
      // Organizar permissões por módulo
      const porModulo = {};
      (response.data || []).forEach(permissao => {
        const modulo = permissao.modulo || 'Outros';
        if (!porModulo[modulo]) {
          porModulo[modulo] = [];
        }
        porModulo[modulo].push(permissao);
      });
      
      setPermissoesPorModulo(porModulo);
      
      // Expandir todos os módulos por padrão
      const expandidos = {};
      Object.keys(porModulo).forEach(modulo => {
        expandidos[modulo] = true;
      });
      setModulosExpandidos(expandidos);
      
    } catch (err) {
      console.error('Erro ao carregar permissões:', err);
    } finally {
      setLoadingPermissoes(false);
    }
  };

  const handleEdit = () => {
    navigate(`/seguranca/perfis/${id}/editar`);
  };

  const handleBack = () => {
    navigate('/seguranca/perfis');
  };

  const toggleModulo = (modulo) => {
    setModulosExpandidos(prev => ({
      ...prev,
      [modulo]: !prev[modulo]
    }));
  };

  const filtrarPermissoes = (permissoes) => {
    if (!filtroPermissoes) return permissoes;
    
    return permissoes.filter(permissao => 
      permissao.descricao?.toLowerCase().includes(filtroPermissoes.toLowerCase()) ||
      permissao.acao?.toLowerCase().includes(filtroPermissoes.toLowerCase()) ||
      permissao.nome?.toLowerCase().includes(filtroPermissoes.toLowerCase())
    );
  };

  const getPermissoesFiltradas = () => {
    const resultado = {};
    Object.entries(permissoesPorModulo).forEach(([modulo, permissoes]) => {
      const permissoesFiltradas = filtrarPermissoes(permissoes);
      if (permissoesFiltradas.length > 0) {
        resultado[modulo] = permissoesFiltradas;
      }
    });
    return resultado;
  };

  // Mapeamento de ícones por módulo
  const getModuleIcon = (modulo) => {
    const icons = {
      'Segurança': faShieldAlt,
      'Usuários': faUsers,
      'Colaboradores': faUsers,
      'Alunos': faGraduationCap,
      'Cursos': faBook,
      'Financeiro': faDollarSign,
      'Estoque': faBoxes,
      'Relatórios': faChartBar,
      'Configurações': faCog,
      'Sistema': faCog
    };
    return icons[modulo] || faKey;
  };

  // Cores por tipo de ação
  const getActionColor = (acao) => {
    const colors = {
      'create': 'text-green-600 bg-green-50',
      'read': 'text-blue-600 bg-blue-50',
      'update': 'text-yellow-600 bg-yellow-50',
      'delete': 'text-red-600 bg-red-50',
      'list': 'text-purple-600 bg-purple-50'
    };
    return colors[acao] || 'text-gray-600 bg-gray-50';
  };

  const getTotalPermissoes = () => permissoes.length;
  const getTotalModulos = () => Object.keys(permissoesPorModulo).length;

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
        {error}
      </div>
    );
  }

  if (!perfil) {
    return (
      <div className="bg-yellow-50 border border-yellow-200 text-yellow-700 px-4 py-3 rounded">
        Perfil não encontrado.
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Cabeçalho */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={handleBack}
            className="text-gray-600 hover:text-gray-800"
          >
                        <FontAwesomeIcon icon={faArrowLeft} className="text-xl" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Visualizar Perfil</h1>
            <p className="text-gray-600">Detalhes do perfil e suas permissões</p>
          </div>
        </div>
        {hasPermission('perfis.update') && (
          <button
            onClick={handleEdit}
            className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg flex items-center space-x-2"
          >
                        <FontAwesomeIcon icon={faPen} />
            <span>Editar</span>
          </button>
        )}
      </div>

      {/* Cards de Resumo */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <FontAwesomeIcon icon={faShieldAlt} className="h-8 w-8 text-blue-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Status</p>
              <p className="text-lg font-semibold text-gray-900">
                {perfil?.ativo ? 'Ativo' : 'Inativo'}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <FontAwesomeIcon icon={faKey} className="h-8 w-8 text-green-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Permissões</p>
              <p className="text-lg font-semibold text-gray-900">
                {getTotalPermissoes()}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <FontAwesomeIcon icon={faBoxes} className="h-8 w-8 text-purple-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Módulos</p>
              <p className="text-lg font-semibold text-gray-900">
                {getTotalModulos()}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <FontAwesomeIcon icon={faChartBar} className="h-8 w-8 text-orange-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Nível</p>
              <p className="text-lg font-semibold text-gray-900">
                {perfil?.nivel_acesso}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Informações do Perfil */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">Informações Básicas</h3>
        </div>
        
        <div className="p-6">
          <dl className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* ID */}
            <div>
              <dt className="text-sm font-medium text-gray-500">ID</dt>
              <dd className="mt-1 text-sm text-gray-900">{perfil.id}</dd>
            </div>

            {/* Nome */}
            <div>
              <dt className="text-sm font-medium text-gray-500">Nome</dt>
              <dd className="mt-1 text-sm text-gray-900">{perfil.nome}</dd>
            </div>

            {/* Descrição */}
            <div className="md:col-span-2">
              <dt className="text-sm font-medium text-gray-500">Descrição</dt>
              <dd className="mt-1 text-sm text-gray-900">
                {perfil.descricao || 'Sem descrição'}
              </dd>
            </div>

            {/* Nível de Acesso */}
            <div>
              <dt className="text-sm font-medium text-gray-500">Nível de Acesso</dt>
              <dd className="mt-1">
                <span className="px-2 py-1 bg-purple-100 text-purple-800 rounded-full text-xs">
                  Nível {perfil.nivel_acesso}
                </span>
              </dd>
            </div>

            {/* Status */}
            <div>
              <dt className="text-sm font-medium text-gray-500">Status</dt>
              <dd className="mt-1">
                <span className={`px-2 py-1 rounded-full text-xs ${
                  perfil.ativo 
                    ? 'bg-green-100 text-green-800' 
                    : 'bg-red-100 text-red-800'
                }`}>
                  {perfil.ativo ? 'Ativo' : 'Inativo'}
                </span>
              </dd>
            </div>

            {/* Data de Criação */}
            <div>
              <dt className="text-sm font-medium text-gray-500">Criado em</dt>
              <dd className="mt-1 text-sm text-gray-900">
                {perfil.created_at ? (() => {
                  const date = new Date(perfil.created_at);
                  const adjustedDate = new Date(date.getTime() + date.getTimezoneOffset() * 60000);
                  return adjustedDate.toLocaleDateString('pt-BR');
                })() : 'Data não disponível'}
              </dd>
            </div>

            {/* Data de Atualização */}
            <div>
              <dt className="text-sm font-medium text-gray-500">Atualizado em</dt>
              <dd className="mt-1 text-sm text-gray-900">
                {perfil.updated_at ? (() => {
                  const date = new Date(perfil.updated_at);
                  const adjustedDate = new Date(date.getTime() + date.getTimezoneOffset() * 60000);
                  return adjustedDate.toLocaleDateString('pt-BR');
                })() : 'Nunca atualizado'}
              </dd>
            </div>
          </dl>
        </div>
      </div>

      {/* Seção de Permissões */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <FontAwesomeIcon icon={faKey} className="text-lg text-green-600" />
              <h3 className="text-lg font-medium text-gray-900">Permissões do Perfil</h3>
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setMostrarResumo(!mostrarResumo)}
                className="text-sm text-gray-600 hover:text-gray-800 flex items-center space-x-1"
              >
                <FontAwesomeIcon icon={mostrarResumo ? faEyeSlash : faEye} />
                <span>{mostrarResumo ? 'Ocultar resumo' : 'Mostrar resumo'}</span>
              </button>
            </div>
          </div>
        </div>

        {/* Resumo de Permissões por Ação */}
        {mostrarResumo && (
          <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
            <h4 className="text-sm font-medium text-gray-700 mb-3">Resumo por Tipo de Ação</h4>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              {['create', 'read', 'update', 'delete', 'list'].map(acao => {
                const count = permissoes.filter(p => p.acao === acao).length;
                return (
                  <div key={acao} className="text-center">
                    <div className={`w-12 h-12 mx-auto rounded-full flex items-center justify-center ${getActionColor(acao)}`}>
                      <span className="text-lg font-bold">{count}</span>
                    </div>
                    <p className="text-xs mt-1 text-gray-600 capitalize">{acao}</p>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <div className="p-6">
          {loadingPermissoes ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
              <span className="ml-3 text-gray-600">Carregando permissões...</span>
            </div>
          ) : (
            <>
              {/* Filtro de Permissões */}
              <div className="mb-6">
                <div className="relative">
                  <FontAwesomeIcon icon={faSearch} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Filtrar permissões..."
                    value={filtroPermissoes}
                    onChange={(e) => setFiltroPermissoes(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  />
                </div>
                {filtroPermissoes && (
                  <p className="text-sm text-gray-600 mt-2">
                    Mostrando {Object.values(getPermissoesFiltradas()).reduce((acc, perms) => acc + perms.length, 0)} de {getTotalPermissoes()} permissões
                  </p>
                )}
              </div>

              {/* Lista de Permissões por Módulo */}
              {Object.keys(getPermissoesFiltradas()).length === 0 ? (
                <div className="text-center py-8">
                  <FontAwesomeIcon icon={faKey} className="text-4xl text-gray-300 mb-4" />
                  <p className="text-gray-500">
                    {filtroPermissoes ? 'Nenhuma permissão encontrada com o filtro aplicado' : 'Este perfil não possui permissões'}
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  {Object.entries(getPermissoesFiltradas()).map(([modulo, permissoes]) => (
                    <div key={modulo} className="border border-gray-200 rounded-lg overflow-hidden">
                      <button
                        onClick={() => toggleModulo(modulo)}
                        className="w-full px-4 py-3 bg-gray-50 hover:bg-gray-100 flex items-center justify-between text-left"
                      >
                        <div className="flex items-center space-x-3">
                          <FontAwesomeIcon icon={getModuleIcon(modulo)} className="text-lg text-gray-600" />
                          <span className="font-medium text-gray-900">{modulo}</span>
                          <span className="bg-gray-200 text-gray-700 px-2 py-1 rounded-full text-xs">
                            {permissoes.length}
                          </span>
                        </div>
                        <FontAwesomeIcon 
                          icon={modulosExpandidos[modulo] ? faEyeSlash : faEye} 
                          className="text-gray-400"
                        />
                      </button>
                      
                      {modulosExpandidos[modulo] && (
                        <div className="divide-y divide-gray-100">
                          {permissoes.map((permissao) => (
                            <div key={permissao.id} className="p-4 hover:bg-gray-50">
                              <div className="flex items-start justify-between">
                                <div className="flex-1">
                                  <div className="flex items-center space-x-3">
                                    <span className={`px-2 py-1 rounded text-xs font-medium ${getActionColor(permissao.acao)}`}>
                                      {permissao.acao}
                                    </span>
                                    <h4 className="text-sm font-medium text-gray-900">
                                      {permissao.descricao}
                                    </h4>
                                  </div>
                                  {permissao.nome && (
                                    <p className="text-xs text-gray-500 mt-1">
                                      Identificador: {permissao.nome}
                                    </p>
                                  )}
                                </div>
                                <FontAwesomeIcon icon={faCheck} className="text-green-500 mt-1" />
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default PerfilView;
